import React, { useState, useEffect } from 'react';
import { Wifi, Home, Building, MapPin, User, Phone, Mail, Calendar, AlertTriangle, Info, Check } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { validateSAPostalCode, formatAndValidateSACellphone, validateEmail } from './validationUtils';

const InstallationStep = ({ formData, updateFormData, installationAddress, errors = {} }) => { // Remove coordinates prop
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipAcknowledged, setTooltipAcknowledged] = useState(false);
  const [localErrors, setLocalErrors] = useState({});
  
  // Auto-show tooltip when "New Installation" is selected
  useEffect(() => {
    if (formData.installationStatus === 'newInstallation') {
      setShowTooltip(true);
    }
  }, [formData.installationStatus]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    let updatedFormData = { ...formData };
    
    // If changing installation status to "New Installation", show tooltip
    if (name === 'installationStatus' && value === 'newInstallation') {
      setShowTooltip(true);
      setTooltipAcknowledged(false);
    }
    
    // Validate postal code
    if (name === 'postalCode') {
      if (value.length > 0) {
        const result = validateSAPostalCode(value);
        if (!result.valid) {
          setLocalErrors(prev => ({ ...prev, postalCode: result.message }));
        } else {
          setLocalErrors(prev => ({ ...prev, postalCode: null }));
        }
      } else {
        setLocalErrors(prev => ({ ...prev, postalCode: null }));
      }
    }
    
    // Validate landlord contact number
    if (name === 'landlordContact') {
      const result = formatAndValidateSACellphone(value);
      updatedValue = result.formatted;
      
      if (!result.valid && value.length > 0) {
        setLocalErrors(prev => ({ ...prev, landlordContact: result.message }));
      } else {
        setLocalErrors(prev => ({ ...prev, landlordContact: null }));
      }
    }
    
    // Validate landlord email
    if (name === 'landlordEmail') {
      if (value.length > 0) {
        const result = validateEmail(value);
        if (!result.valid) {
          setLocalErrors(prev => ({ ...prev, landlordEmail: result.message }));
        } else {
          setLocalErrors(prev => ({ ...prev, landlordEmail: null }));
        }
      } else {
        setLocalErrors(prev => ({ ...prev, landlordEmail: null }));
      }
    }
    
    updatedFormData[name] = updatedValue;
    updateFormData(updatedFormData);
  };
  
  // Acknowledge tooltip
  const acknowledgeTooltip = () => {
    setTooltipAcknowledged(true);
    // Update form data to include the acknowledgment
    updateFormData({
      ...formData,
      tooltipAcknowledged: true
    });
    // Don't hide the tooltip, just mark it as acknowledged
  };
  
  // Calculate minimum date based on installation type
  const getMinDate = () => {
    const today = new Date();
    
    if (formData.installationStatus === 'migration') {
      // For migrations/activations: tomorrow
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow;
    } else {
      // For new installations: 3 days from now (today + 3 days = 4th day)
      const threeDaysLater = new Date(today);
      threeDaysLater.setDate(today.getDate() + 3); // This actually gives us the 4th day
      
      // Ensure it's actually 3 full days later by checking
      const daysDiff = Math.floor((threeDaysLater - today) / (1000 * 60 * 60 * 24));
      if (daysDiff < 3) {
        // Add one more day if needed
        threeDaysLater.setDate(threeDaysLater.getDate() + 1);
      }
      
      return threeDaysLater;
    }
  };
  
  // Handle date change
  const handleDateChange = (date) => {
    updateFormData({
      ...formData,
      installationDate: date
    });
  };
  
  // Get the appropriate label based on installation type
  const getDateLabel = () => {
    if (formData.installationStatus === 'migration') {
      return "Activation Date";
    } else if (formData.installationStatus === 'newInstallation') {
      return "Installation Date";
    } else {
      return "Preferred Installation/Activation Date";
    }
  };

  // Pre-fill address fields from installationAddress if available
  React.useEffect(() => {
    if (installationAddress) {
      // Extract address components from installationAddress
      // This is a simplified example - you may need to adjust based on your actual address format
      const addressComponents = {
        // Extract street number and street name from mainText
        // Assuming format like "123 Main Road"
        streetNumber: '',
        streetName: '',
        suburb: '',
        city: '',
        postalCode: '',
        provinceName: '', // Add province name
        latitude: '', // Add latitude
        longitude: '' // Add longitude
      };

      // Extract street number and name from mainText
      if (installationAddress.mainText) {
        // Try to match a pattern like "123 Main Road"
        // Use an improved regex that handles street numbers with letters (e.g., "54b")
        const streetMatch = installationAddress.mainText.match(/^\s*(\d+[a-zA-Z]?)\s+(.*)\s*$/);
        if (streetMatch) {
          // If we can extract a number at the beginning
          addressComponents.streetNumber = streetMatch[1];
          addressComponents.streetName = streetMatch[2];
        } else {
          // If no number at the beginning, use the whole string as street name
          addressComponents.streetName = installationAddress.mainText;
        }
      }
      
      // If there's a secondaryText, try to extract suburb, city, etc.
      if (installationAddress.secondaryText) {
        const parts = installationAddress.secondaryText.split(',').map(part => part.trim());
        if (parts.length >= 1) addressComponents.suburb = parts[0];
        if (parts.length >= 2) addressComponents.city = parts[1];
        
        // Try to extract postal code (assuming it's a number at the end)
        const postalCodeMatch = installationAddress.secondaryText.match(/\d{4,5}/);
        if (postalCodeMatch) {
          addressComponents.postalCode = postalCodeMatch[0];
        }
      }
      
      // Extract province name from address_components
      if (installationAddress?.placeDetails?.address_components) {
        // Try to find the province from address_components
        const provinceComponent = installationAddress.placeDetails.address_components.find(
          component => component.types.includes('administrative_area_level_1')
        );
        if (provinceComponent) {
          addressComponents.provinceName = provinceComponent.long_name;
        }
        
        // Try to find the postal code from address_components
        const postalCodeComponent = installationAddress.placeDetails.address_components.find(
          component => component.types.includes('postal_code')
        );
        if (postalCodeComponent) {
          addressComponents.postalCode = postalCodeComponent.long_name;
        }
      }
      
      // If province is still empty, try to use the direct province field
      if (!addressComponents.provinceName && installationAddress?.placeDetails?.province) {
        addressComponents.provinceName = installationAddress.placeDetails.province;
      }
      
      // If province is still empty, try to extract it from secondaryText
      if (!addressComponents.provinceName && installationAddress.secondaryText) {
        // Try to extract province from the secondary text (e.g., "Suburb, City, Province")
        const parts = installationAddress.secondaryText.split(',').map(part => part.trim());
        if (parts.length >= 3) {
          addressComponents.provinceName = parts[2];
        }
      }
      
      // If province is still empty and we have a city, make an educated guess
      if (!addressComponents.provinceName && addressComponents.city) {
        const city = addressComponents.city.toLowerCase();
        
        // Map common cities to provinces
        if (city.includes('durban') || city.includes('pietermaritzburg') || city.includes('kzn')) {
          addressComponents.provinceName = 'KwaZulu-Natal';
        } else if (city.includes('cape town') || city.includes('stellenbosch')) {
          addressComponents.provinceName = 'Western Cape';
        } else if (city.includes('johannesburg') || city.includes('pretoria') || city.includes('gauteng')) {
          addressComponents.provinceName = 'Gauteng';
        }
      }
      
      // Default to Gauteng if still empty
      if (!addressComponents.provinceName) {
        addressComponents.provinceName = 'Gauteng';
      }
      
      // Extract coordinates as well
      if (installationAddress?.placeDetails?.geometry?.location) {
        addressComponents.latitude = installationAddress.placeDetails.geometry.location.lat;
        addressComponents.longitude = installationAddress.placeDetails.geometry.location.lng;
      }

      updateFormData({
        ...formData,
        ...addressComponents
      });
    }
  }, [installationAddress]);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Installation Details</h2>
      <p className="text-gray-400 text-sm">Please provide details about your installation preferences and address.</p>
      
      {/* WiFi Router Selection */}
      <div className="space-y-3 pt-4 border-t border-gray-700">
        <label className="block text-sm font-medium text-gray-300">WiFi Router Selection</label>
        <div className="space-y-3">
          <label className="flex items-start p-4 bg-gray-800 rounded-lg border border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors">
            <input
              type="radio"
              name="routerOption"
              value="freeRouter"
              checked={formData.routerOption === 'freeRouter'}
              onChange={handleChange}
              className="h-4 w-4 mt-1 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <div className="ml-3">
              <span className="block text-white font-medium">Free to Use Router</span>
              <span className="block text-sm text-gray-400 mt-1">
                We'll provide a router at no additional cost. 
                <span className="text-amber-400 ml-1">R250 Shipping Fee applies</span>
              </span>
            </div>
          </label>
          
          <label className="flex items-start p-4 bg-gray-800 rounded-lg border border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors">
            <input
              type="radio"
              name="routerOption"
              value="ownRouter"
              checked={formData.routerOption === 'ownRouter'}
              onChange={handleChange}
              className="h-4 w-4 mt-1 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <div className="ml-3">
              <span className="block text-white font-medium">I will use my own router</span>
              <span className="block text-sm text-gray-400 mt-1">
                Use your existing compatible router
              </span>
            </div>
          </label>
        </div>
        {errors.routerOption && <p className="text-red-500 text-xs mt-1">{errors.routerOption}</p>}
      </div>
      
      {/* Existing Installation Status */}
      <div className="space-y-3 pt-4 border-t border-gray-700">
        <label className="block text-sm font-medium text-gray-300">Existing Installation Status</label>
        <select
          name="installationStatus"
          value={formData.installationStatus || ''}
          onChange={handleChange}
          className="w-full py-2 px-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="">Please select</option>
          <option value="notSure">I am not sure</option>
          <option value="migration">Migration (switching from another provider)</option>
          <option value="newInstallation">New Installation</option>
        </select>
        {errors.installationStatus && <p className="text-red-500 text-xs mt-1">{errors.installationStatus}</p>}
      </div>
      
      {/* Installation/Activation Date */}
      {formData.installationStatus && (
        <div className="space-y-3 mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="flex items-center justify-between">
            <label htmlFor="installationDate" className="block text-sm font-medium text-gray-300">
              {getDateLabel()} <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                className="text-gray-400 hover:text-white"
                onClick={() => setShowTooltip(!showTooltip)}
              >
                <Info className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {/* Tooltip/Warning for installation dates */}
          {showTooltip && (
            <div className="mb-4 p-3 bg-amber-900/30 border border-amber-800/30 rounded-lg text-xs text-gray-300">
              <div className="flex items-start mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
                <p>
                  This date is best effort and dependent on the fibre provider who performs the installation.
                  We will not be held liable for any delays outside of our control.
                </p>
              </div>
              {!tooltipAcknowledged && (
                <button
                  type="button"
                  className="w-full mt-2 py-1.5 px-2 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 font-medium"
                  onClick={acknowledgeTooltip}
                >
                  I acknowledge and accept
                </button>
              )}
              {tooltipAcknowledged && (
                <div className="w-full mt-2 py-1 px-2 bg-green-900/30 text-green-400 text-xs rounded flex items-center">
                  <Check className="h-3 w-3 mr-1" />
                  Acknowledged
                </div>
              )}
            </div>
          )}
          
          <div className="relative custom-datepicker-container">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
              <Calendar className="h-4 w-4 text-gray-400" />
            </div>
            
            <DatePicker
              selected={formData.installationDate}
              onChange={handleDateChange}
              minDate={getMinDate()}
              dateFormat="yyyy-MM-dd"
              className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                errors.installationDate ? 'border-red-500' : 'border-gray-600'
              } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
              disabled={formData.installationStatus === 'newInstallation' && !tooltipAcknowledged}
              placeholderText="Select date"
              popperClassName="custom-datepicker-popper"
              calendarClassName="custom-calendar"
              dayClassName={date => "custom-day"}
              wrapperClassName="custom-datepicker-wrapper"
            />
            
            {/* Custom styling for DatePicker */}
            <style jsx global>{`
              /* DatePicker Container */
              .custom-datepicker-wrapper {
                width: 100%;
              }
              
              /* Calendar Popup */
              .react-datepicker {
                background-color: #1f2937 !important;
                border: 1px solid #374151 !important;
                border-radius: 0.5rem !important;
                font-family: inherit !important;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3) !important;
              }
              
              /* Header */
              .react-datepicker__header {
                background-color: #111827 !important;
                border-bottom: 1px solid #374151 !important;
                padding-top: 0.5rem !important;
                color: white !important;
              }
              
              /* Month text */
              .react-datepicker__current-month {
                color: white !important;
                font-weight: 600 !important;
                padding: 0.5rem 0 !important;
              }
              
              /* Day names */
              .react-datepicker__day-name {
                color: #9ca3af !important;
                margin: 0.2rem !important;
                width: 2rem !important;
                height: 2rem !important;
                line-height: 2rem !important;
              }
              
              /* Days */
              .react-datepicker__day {
                color: white !important;
                margin: 0.2rem !important;
                width: 2rem !important;
                height: 2rem !important;
                line-height: 2rem !important;
                border-radius: 9999px !important;
              }
              
              /* Hover state */
              .react-datepicker__day:hover {
                background-color: #4b5563 !important;
              }
              
              /* Selected day */
              .react-datepicker__day--selected {
                background-color: #9333ea !important;
                font-weight: bold !important;
              }
              
              /* Today */
              .react-datepicker__day--today {
                font-weight: bold !important;
                border: 1px solid #9333ea !important;
              }
              
              /* Disabled days */
              .react-datepicker__day--disabled {
                color: #6b7280 !important;
                cursor: not-allowed !important;
              }
              
              /* Navigation */
              .react-datepicker__navigation {
                top: 0.75rem !important;
              }
              
              .react-datepicker__navigation-icon::before {
                border-color: #9ca3af !important;
              }
              
              .react-datepicker__navigation:hover *::before {
                border-color: white !important;
              }
            `}</style>
          </div>
          {errors.installationDate && <p className="text-red-500 text-xs mt-1">{errors.installationDate}</p>}
          
          <p className="text-xs text-amber-400 flex items-center mt-2">
            <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
            {formData.installationStatus === 'migration'
              ? "Activations can only be scheduled from tomorrow onwards"
              : "New installations can only be scheduled at least 3 days from today"}
          </p>
          
          {formData.installationStatus === 'newInstallation' && !tooltipAcknowledged && (
            <p className="text-xs text-red-400 mt-2">
              Please acknowledge the installation notice above to continue
            </p>
          )}
        </div>
      )}
      
      {/* Installation Address */}
      <div className="space-y-4 pt-4 border-t border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-md font-medium text-gray-300">Installation Address</h3>
          {installationAddress && (
            <span className="text-xs text-gray-400">
              Pre-filled from coverage check
            </span>
          )}
        </div>
        
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-1">
              <label htmlFor="streetNumber" className="block text-sm font-medium text-gray-300">
                Street Number
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <input
                type="text"
                id="streetNumber"
                name="streetNumber"
                value={formData.streetNumber || ''}
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <label htmlFor="streetName" className="block text-sm font-medium text-gray-300">
                Street Name
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="streetName"
                  name="streetName"
                  value={formData.streetName || ''}
                  readOnly
                  className="w-full py-2 pl-10 pr-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="suburb" className="block text-sm font-medium text-gray-300">
                Suburb
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <input
                type="text"
                id="suburb"
                name="suburb"
                value={formData.suburb || ''}
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
            
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="city" className="block text-sm font-medium text-gray-300">
                City
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city || ''}
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="provinceName" className="block text-sm font-medium text-gray-300">
                Province
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <input
                type="text"
                id="provinceName"
                name="provinceName"
                value={formData.provinceName || ''}
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="postalCode" className="block text-sm font-medium text-gray-300">
                Postal Code
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode || ''}
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-300">
                Latitude
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                value={formData.latitude || ''}
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-300">
                Longitude
                <span className="ml-2 text-xs text-gray-400">(From coverage check)</span>
              </label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                value={formData.longitude || ''}
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="locationId" className="block text-sm font-medium text-gray-300">
                Location ID
                <span className="ml-2 text-xs text-gray-400">(Based on province)</span>
              </label>
              <input
                type="text"
                id="locationId"
                name="locationId"
                value={
                  formData.provinceName
                    ? (formData.provinceName.toLowerCase().includes('kwazulu') ||
                       formData.provinceName.toLowerCase().includes('kzn') ||
                       formData.provinceName.toLowerCase().includes('durban')
                         ? '7 - KZN'
                         : formData.provinceName.toLowerCase().includes('cape')
                           ? '6 - CPT'
                           : '5 - JHB')
                    : '5 - JHB (Default)'
                }
                readOnly
                className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-lg text-white cursor-not-allowed opacity-80"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Property Type */}
      <div className="space-y-3 pt-4 border-t border-gray-700">
        <label className="block text-sm font-medium text-gray-300">Property Type</label>
        <div className="flex gap-4">
          <label className="flex items-center p-3 bg-gray-800 rounded-lg border border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors">
            <input
              type="radio"
              name="propertyType"
              value="freestanding"
              checked={formData.propertyType === 'freestanding'}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <div className="ml-2 flex items-center">
              <Home className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-white">Free standing house</span>
            </div>
          </label>
          
          <label className="flex items-center p-3 bg-gray-800 rounded-lg border border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors">
            <input
              type="radio"
              name="propertyType"
              value="complex"
              checked={formData.propertyType === 'complex'}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <div className="ml-2 flex items-center">
              <Building className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-white">Complex/Estate</span>
            </div>
          </label>
        </div>
        {errors.propertyType && <p className="text-red-500 text-xs mt-1">{errors.propertyType}</p>}
        
        {/* Conditional Complex/Estate fields */}
        {formData.propertyType === 'complex' && (
          <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="complex" className="block text-sm font-medium text-gray-300">
                  Complex/Estate/Apartment <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="complex"
                  name="complex"
                  value={formData.complex || ''}
                  onChange={handleChange}
                  className={`w-full py-2 px-3 bg-gray-700 border ${
                    errors.complex ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  placeholder="Complex, estate or apartment name"
                />
                {errors.complex && <p className="text-red-500 text-xs mt-1">{errors.complex}</p>}
              </div>
              
              <div className="space-y-2">
                <label htmlFor="unitNumber" className="block text-sm font-medium text-gray-300">
                  Unit Number <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="unitNumber"
                  name="unitNumber"
                  value={formData.unitNumber || ''}
                  onChange={handleChange}
                  className={`w-full py-2 px-3 bg-gray-700 border ${
                    errors.unitNumber ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  placeholder="Unit or apartment number"
                />
                {errors.unitNumber && <p className="text-red-500 text-xs mt-1">{errors.unitNumber}</p>}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Property Ownership */}
      <div className="space-y-3 pt-4 border-t border-gray-700">
        <label className="block text-sm font-medium text-gray-300">Property Ownership</label>
        <div className="flex gap-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="propertyOwnership"
              value="owned"
              checked={formData.propertyOwnership === 'owned'}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <span className="ml-2 text-gray-300">Owned</span>
          </label>
          
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="propertyOwnership"
              value="rented"
              checked={formData.propertyOwnership === 'rented'}
              onChange={handleChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-600 bg-gray-700"
            />
            <span className="ml-2 text-gray-300">Rented</span>
          </label>
        </div>
        {errors.propertyOwnership && <p className="text-red-500 text-xs mt-1">{errors.propertyOwnership}</p>}
      </div>
      
      {/* Conditional Landlord Details */}
      {formData.propertyOwnership === 'rented' && (
        <div className="space-y-4 pt-4 border-t border-gray-700 bg-gray-800 p-4 rounded-lg">
          <h3 className="text-md font-medium text-gray-300">Landlord Details</h3>
          <p className="text-gray-400 text-xs">Since you're renting, we need your landlord's details for installation approval.</p>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <label htmlFor="landlordName" className="block text-sm font-medium text-gray-300">Landlord Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="landlordName"
                  name="landlordName"
                  value={formData.landlordName || ''}
                  onChange={handleChange}
                  className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                    errors.landlordName ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  placeholder="Landlord's full name"
                />
              </div>
              {errors.landlordName && <p className="text-red-500 text-xs mt-1">{errors.landlordName}</p>}
            </div>
            
            <div className="space-y-2">
              <label htmlFor="landlordContact" className="block text-sm font-medium text-gray-300">Landlord Contact Number</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="landlordContact"
                  name="landlordContact"
                  value={formData.landlordContact || ''}
                  onChange={handleChange}
                  className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                    errors.landlordContact || localErrors.landlordContact ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  placeholder="South African cellphone number"
                />
              </div>
              {(errors.landlordContact || localErrors.landlordContact) && (
                <p className="text-red-500 text-xs mt-1">{errors.landlordContact || localErrors.landlordContact}</p>
              )}
              <p className="text-gray-400 text-xs">Format: 0834637609</p>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="landlordEmail" className="block text-sm font-medium text-gray-300">Landlord Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="landlordEmail"
                  name="landlordEmail"
                  value={formData.landlordEmail || ''}
                  onChange={handleChange}
                  className={`w-full py-2 pl-10 pr-3 bg-gray-700 border ${
                    errors.landlordEmail || localErrors.landlordEmail ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  placeholder="Landlord's email address"
                />
              </div>
              {(errors.landlordEmail || localErrors.landlordEmail) && (
                <p className="text-red-500 text-xs mt-1">{errors.landlordEmail || localErrors.landlordEmail}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallationStep;